// One stock-location parser for the frontend — the twin of wms_core.parse_location
// (backend). "M 23", "m23" and "M23" are the same place. Single letters A–Z are aisle
// faces; Ä and Ö alone are the special locations; two letters ("KC 5") are old zone
// codes; a bare number is not a location. Claude, 2026-09-25.

export type LocationKind = 'section' | 'special' | 'zone' | 'invalid' | 'none';

export interface ParsedLocation {
  code: string;          // normalised: 'M23'
  display: string;       // label form: 'M 23'
  letter: string | null;
  number: number | null;
  level: number | null;
  kind: LocationKind;
}

// Ä (sold supplier stock by P&P), Ö (the P&P table, socks), Å (returned pieces, numbered shelves — 1.11); a letter
// alone or with a number is 'special'
export const SPECIAL_LOCATIONS = ['Ä', 'Ö', 'Å'];
// a NAMED special place — a word of 3–12 letters, e.g. CAMPAIGN (3.11); the floor model says what it does
const NAMED_SPECIAL_RE = /^[A-ZÅÄÖ]{3,12}$/;
const LOC_RE = /^([A-ZÅÄÖ]{1,2})(\d{1,4})(?:\.(\d+))?$/;

export function normLocation(s: string | null | undefined): string {
  return s ? String(s).replace(/\s+/g, '').toUpperCase() : '';
}

export function parseLocation(s: string | null | undefined): ParsedLocation {
  const code = normLocation(s);
  if (!code) return { code: '', display: '', letter: null, number: null, level: null, kind: 'none' };
  if (SPECIAL_LOCATIONS.includes(code) || NAMED_SPECIAL_RE.test(code)) return { code, display: code, letter: code, number: null, level: null, kind: 'special' };
  const m = LOC_RE.exec(code);
  if (!m) return { code, display: String(s).trim(), letter: null, number: null, level: null, kind: 'invalid' };
  const letter = m[1], num = parseInt(m[2], 10), level = m[3] ? parseInt(m[3], 10) : null;
  return {
    code, letter, number: num, level,
    display: `${letter} ${num}${level !== null ? `.${level}` : ''}`,
    kind: SPECIAL_LOCATIONS.includes(letter) ? 'special' : letter.length === 1 ? 'section' : 'zone',
  };
}

/** 'M23' → 'M 23' for display; anything unparseable comes back as given. */
export function displayLocation(s: string | null | undefined): string {
  return parseLocation(s).display;
}
